import { API } from '../../api';
import { Button, Card, Col, Input, Row, Spin, Tree, notification } from 'antd';
import {
  DatabaseOutlined,
  DownOutlined,
  FieldBinaryOutlined,
  FieldStringOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { sortArrOfObjectsByStr } from '../../utils/util';
import React, { useEffect, useState } from 'react';

/**
 * This is mock data right now, and unused. It gives one possible way we could display action types
 * and their fields in a tree-like view.
 */
// eslint-disable-next-line no-unused-vars
const treeDataMOCK = [
  {
    title: 'List Files from S3',
    key: '0',
    icon: <DatabaseOutlined />,
    children: [
      {
        title: 'Input',
        key: '0-1',
        icon: <PlayCircleOutlined />,
        children: [
          {
            title: 'jobId',
            key: '0-0-0',
            icon: <FieldBinaryOutlined />,
          },
          {
            title: 'actionId',
            key: '0-0-1',
            icon: <FieldBinaryOutlined />,
          },
          {
            title: 'actionType',
            key: '0-0-2',
            icon: <FieldStringOutlined />,
          },
          {
            title: 'actionPayload',
            key: '0-0-3',
            icon: <DatabaseOutlined />,
            children: [
              {
                title: 's3Region',
                key: '0-0-3-0',
                icon: <FieldStringOutlined />,
              },
              {
                title: 's3Bucket',
                key: '0-0-3-1',
                icon: <FieldStringOutlined />,
              },
              {
                title: 's3Prefix',
                key: '0-0-3-2',
                icon: <FieldStringOutlined />,
              },
            ],
          },
        ],
      },
      {
        title: 'Output',
        key: '0-2',
        icon: <PlayCircleOutlined />,
        children: [
          {
            title: 'status',
            key: '0-2-0',
            icon: <FieldStringOutlined />,
          },
          {
            title: 'outputPayload',
            key: '0-2-1',
            icon: <DatabaseOutlined />,
          },
        ],
      },
    ],
  },
];

const IngestionConfigurationTab = props => {
  const { selectedClient } = props;
  const [currentActionType, setCurrentActionType] = useState('');
  const [actionTypes, setActionTypes] = useState([]);
  const [actionTypeConfigs, setActionTypeConfigs] = useState([]);
  const [treeData, setTreeData] = useState([]);

  const [isLoading, setLoading] = useState(false);
  const [actionTypeMap, setActionTypeMap] = useState({});
  const [currentActionTypeConfig, setCurrentActionTypeConfig] = useState({});
  const [currentActionTypeSubscriber, setCurrentActionTypeSubscriber] = useState('');

  /**
   * Basic construction of tree. Currently creates root-level elements (action types).
   * This can be expanded in the future to recurse through the tree and include input/output fields as well.
   */
  const createTreeData = () => {
    const treeDataArr = [];
    const actionTypesByName = {};
    const actionTypesConfigured = actionTypeConfigs.map(d => d.actionType.name);

    for (let i = 0; i < actionTypes.length; i += 1) {
      const treeNode = {
        title: actionTypes[i].name,
        key: i,
        icon: <DatabaseOutlined />,
      };

      if (!actionTypesConfigured.includes(actionTypes[i].name)) {
        treeNode.style = {
          backgroundColor: 'yellow',
        };
      }

      // Add traversal method in future for this
      treeDataArr.push(treeNode);
      // Map action type name back to action type data
      actionTypesByName[actionTypes[i].name] = actionTypes[i];
    }

    setTreeData(treeDataArr);
    setActionTypeMap(actionTypesByName);
  };

  /**
   * Fetch info on all action types.
   * NOTE: Eventually we can also get input/output fields as well.
   */
  const getActionTypes = async () => {
    setLoading(true);

    try {
      const { data } = await API.get(`/dis/actionTypes?clientId=${selectedClient.id}`);
      setActionTypes(sortArrOfObjectsByStr(data.results, 'name'));
    } catch (err) {
      notification.error({
        message: 'Error',
        description: 'Failed to get Action Type',
        duration: 3,
        placement: 'topLeft',
      });
    }

    setLoading(false);
  };

  /**
   * Get all action type configs for this client. This is used to indicate to the user which
   * action types do not yet have a subscriber set.
   */
  const getActionTypeConfigs = async () => {
    try {
      const { data } = await API.get(`/dis/actionTypeConfigs?clientId=${selectedClient.id}`);
      setActionTypeConfigs(data.results);
    } catch {
      notification.error({
        message: 'Error',
        description: 'Failed to get Action Type Configs',
        duration: 3,
        placement: 'topLeft',
      });
    }
  };

  /**
   * Assumes that the title of the node is the name of the action type.
   */
  const handleSelectActionType = (_, d) => {
    setCurrentActionType(d.node.title);
    const selectedActionTypeConfig = actionTypeConfigs.find(
      el => el.actionType.name === d.node.title,
    );

    if (selectedActionTypeConfig) {
      setCurrentActionTypeConfig(selectedActionTypeConfig);
      setCurrentActionTypeSubscriber(selectedActionTypeConfig.subscriber);
    } else {
      setCurrentActionTypeConfig({});
      setCurrentActionTypeSubscriber('');
    }
  };

  const updateActionTypeSubscriber = async () => {
    // if no ID for currentActionTypeConfig, there's no action type config, so we POST
    const httpMethodName = currentActionTypeConfig.id === undefined ? 'post' : 'patch';
    const method = API[httpMethodName];
    let urlSuffix = '';

    if (httpMethodName === 'post') {
      urlSuffix = `?clientId=${selectedClient.id}`;
    } else {
      urlSuffix = `?actionType=${currentActionTypeConfig.id}&clientId=${selectedClient.id}`;
    }

    let reqBody;

    const actionType = actionTypeMap[currentActionType];

    if (httpMethodName === 'patch') {
      reqBody = { subscriber: currentActionTypeSubscriber };
    }

    // note that we're passing in tenant by name here.
    if (httpMethodName === 'post') {
      reqBody = {
        subscriber: currentActionTypeSubscriber,
        actionType: actionType.name,
        tenant: selectedClient.name,
      };
    }

    try {
      const res = await method(`/dis/actionTypeConfigs${urlSuffix}`, reqBody);
      notification.success({
        message: 'Subscriber updated',
        description: '',
        duration: 3,
        placement: 'topLeft',
      });

      // update actionTypeConfigs state variable to reflect change
      if (httpMethodName === 'post' && !actionTypeConfigs.find(el => el.id === res.data[0].id)) {
        setActionTypeConfigs([...actionTypeConfigs, res.data[0]]);
      } else {
        const updatedActionTypeConfigs = actionTypeConfigs.map(el => {
          return el.id === res.data.id ? res.data : el;
        });
        setActionTypeConfigs(updatedActionTypeConfigs);
      }
    } catch {
      notification.error({
        message: 'Error',
        description: 'Failed to get Action Type',
        duration: 3,
        placement: 'topLeft',
      });
    }
  };

  // on mount, fetch action types
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await getActionTypes();
      await getActionTypeConfigs();
      setLoading(false);
    };

    if (selectedClient) fetchData();
  }, [selectedClient]);

  useEffect(() => {
    createTreeData();
  }, [actionTypeConfigs]);

  return (
    <>
      <h3>Action Type Configuration</h3>
      <p>All action types need a subscriber. </p>
      <hr />
      <Spin spinning={isLoading}>
        <Row>
          <Col span={8}>
            <h4>Action Types</h4>
            <Tree
              showIcon
              switcherIcon={<DownOutlined />}
              treeData={treeData}
              onSelect={handleSelectActionType}
            />
          </Col>
          <Col span={1} />
          <Col span={15}>
            {currentActionType && (
              <Card title={`${currentActionType}`}>
                <h4>Subscriber</h4>
                <Input
                  placeholder="Subscriber URI"
                  value={`${currentActionTypeSubscriber}`}
                  onChange={e => setCurrentActionTypeSubscriber(e.target.value)}
                  data-cy="input-subscriber-uri"
                />
                <Button
                  style={{ marginTop: '12px' }}
                  type="primary"
                  onClick={updateActionTypeSubscriber}
                  data-cy="save-subscriber-uri"
                >
                  Set
                </Button>
              </Card>
            )}
          </Col>
        </Row>
      </Spin>
    </>
  );
};

export default IngestionConfigurationTab;
